import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { debounce } from "lodash";
import TextareaAutosize from "react-textarea-autosize";
import { cn } from "@/utils/utils";
import {
  DealQuestionnaireQuestionSettings,
  DealsQuestionType
} from "@/pages/deals/types";
import TextEditor from "@/components/controls/TextEditor/TextEditor";

interface QuestionInputProps {
  value?: any;
  onChange?: (value: any) => void;
  question?: DealQuestionnaireQuestionSettings;
}

export const TextQuestionInput: React.FC<QuestionInputProps> = ({
  value = "",
  onChange,
  question
}) => {
  const { t } = useTranslation();
  const [localValue, setLocalValue] = useState(value || "");

  const inputPlaceholder =
    question?.responseType?.placeholder ||
    t("deals.dealView.textQuestionPlaceholder");

  const debouncedOnChange = useMemo(
    () =>
      debounce((newValue: string) => {
        onChange?.(newValue);
      }, 500),
    [onChange]
  );

  useEffect(() => {
    if (localValue !== value) {
      debouncedOnChange(localValue);
    }
    return () => debouncedOnChange.cancel();
  }, [localValue, debouncedOnChange]);

  useEffect(() => {
    if (value !== localValue) {
      setLocalValue(value || "");
    }
  }, [value]);

  const handleChange = useCallback((newValue: string) => {
    setLocalValue(newValue);
  }, []);

  const handleBlur = useCallback(
    (newValue: string) => {
      const trimmedValue = newValue.trim();
      setLocalValue(trimmedValue);
      onChange?.(trimmedValue);
    },
    [onChange]
  );

  return (
    <>
      {question?.responseType?.type === DealsQuestionType.TEXT ? (
        <TextareaAutosize
          value={localValue}
          onChange={(e) => handleChange(e.target.value)}
          onBlur={(e) => handleBlur(e.target.value)}
          placeholder={inputPlaceholder}
          minRows={
            question?.responseType?.type === DealsQuestionType.TEXT ? 1 : 4
          }
          className={cn(
            "w-full text-base text-primary placeholder:text-tertiary p-2 bg-transparent border",
            "border-border-border2",
            "rounded-md resize-none focus:outline-none focus:border-primary transition-[border-color] duration-200 ease-in-out"
          )}
        />
      ) : (
        <TextEditor
          value={localValue}
          onBlur={handleBlur}
          placeholder={inputPlaceholder}
          variant="form"
        />
      )}
    </>
  );
};

export default TextQuestionInput;
